import { LegacyGaEventNames } from '../constants/legacy-ga-events.constants'
import { AnalyticsEvents } from '../types/events'
import { sanitizeGaEventName } from '../utils/sanitize-ga-event-name.utils'

export type AnalyticsProviderKey = 'googleAnalytics' | 'backendAnalytics' | 'pendoAnalytics'
export type AnalyticsAccess = 'anonymous' | 'authenticated'
export type AnalyticsDeliveryMode = 'immediate' | 'transactional'
export type AnalyticsContextMode = 'none' | 'route'
export type AnalyticsIdentityRequirement = 'none' | 'actor' | 'route-contact'

export interface AnalyticsDeliveryPolicy {
  provider: AnalyticsProviderKey
  eventName: string
  access: AnalyticsAccess
  delivery: AnalyticsDeliveryMode
  context: AnalyticsContextMode
  requiresIdentity: AnalyticsIdentityRequirement
}

const GA_EVENT_NAMES: Record<string, string> = {
  [AnalyticsEvents.LOGIN_SUCCESS]: LegacyGaEventNames.CUSTOM_LOGIN_EVENT,
  [AnalyticsEvents.LOGIN_FAILED]: 'login_failed',
  [AnalyticsEvents.LOGOUT]: LegacyGaEventNames.LOGOUT,
  [AnalyticsEvents.SIGNUP_COMPLETED]: 'sign_up',
  [AnalyticsEvents.POST_CREATED]: LegacyGaEventNames.POST_CREATED,
  [AnalyticsEvents.POST_EDITED]: 'post_edited_event',
  [AnalyticsEvents.POST_DELETED]: 'post_deleted_event',
  [AnalyticsEvents.COMMENT_CREATED]: LegacyGaEventNames.COMMENT_CREATED,
  [AnalyticsEvents.COMMENT_EDITED]: 'comment_edited_event',
  [AnalyticsEvents.COMMENT_DELETED]: 'comment_deleted_event',
  [AnalyticsEvents.REACTION_ADDED]: LegacyGaEventNames.CREATE_REACTIONS,
  [AnalyticsEvents.REACTION_REMOVED]: LegacyGaEventNames.DELETE_REACTIONS,
  [AnalyticsEvents.GROUP_JOINED]: LegacyGaEventNames.GROUP_JOINED,
  [AnalyticsEvents.LESSON_COMPLETED]: LegacyGaEventNames.LESSON_COMPLETED,
  [AnalyticsEvents.LESSON_STARTED]: LegacyGaEventNames.LESSON_OPENED,
  [AnalyticsEvents.EVENT_CREATED]: LegacyGaEventNames.EVENT_CREATED,
  [AnalyticsEvents.EVENT_RSVP_YES]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.EVENT_RSVP_NO]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.MEDIA_UPLOADED]: LegacyGaEventNames.MEDIA_UPLOADED,
  [AnalyticsEvents.COURSE_VIEWED]: LegacyGaEventNames.COMMUNITY_COURSE_OPENED,
  [AnalyticsEvents.SEARCH_PERFORMED]: LegacyGaEventNames.GOKOLLAB_SEARCH,
  [AnalyticsEvents.SEARCH_RESULT_CLICKED]: LegacyGaEventNames.GOKOLLAB_SEARCH,
  [AnalyticsEvents.CHAT_MESSAGE_SENT]: LegacyGaEventNames.CHAT_MESSAGE_SENT,
  [AnalyticsEvents.CHAT_MESSAGE_FAILED]: LegacyGaEventNames.CHAT_MESSAGE_FAILED,
  [AnalyticsEvents.CHAT_BLOCKED]: LegacyGaEventNames.CHAT_BLOCKED,
  [AnalyticsEvents.CHAT_CREATED]: LegacyGaEventNames.CHAT_CREATED,
  [AnalyticsEvents.GOKOLLAB_HOMEPAGE_LAND]: LegacyGaEventNames.GOKOLLAB_HOMEPAGE_LAND,
  [AnalyticsEvents.GOKOLLAB_LISTING_VIEW]: LegacyGaEventNames.GOKOLLAB_LISTING_VIEW,
  [AnalyticsEvents.GOKOLLAB_LISTING_JOINED]: LegacyGaEventNames.GOKOLLAB_LISTING_JOINED,
  [AnalyticsEvents.GOKOLLAB_COMMUNITIES_VIEW]: LegacyGaEventNames.GOKOLLAB_COMMUNITIES_VIEW,
  [AnalyticsEvents.GOKOLLAB_COURSES_VIEW]: LegacyGaEventNames.GOKOLLAB_COURSES_VIEW,
  [AnalyticsEvents.GOKOLLAB_CREATORS_VIEW]: LegacyGaEventNames.GOKOLLAB_CREATORS_VIEW,
  [AnalyticsEvents.GOKOLLAB_CATEGORY_SWITCH]: LegacyGaEventNames.GOKOLLAB_CATEGORY_SWITCH,
  [AnalyticsEvents.GOKOLLAB_COURSE_CREATE]: LegacyGaEventNames.GOKOLLAB_COURSE_CREATE,
  [AnalyticsEvents.GOKOLLAB_COMMUNITY_CREATE]: LegacyGaEventNames.GOKOLLAB_COMMUNITY_CREATE,
  [AnalyticsEvents.LESSON_OPENED]: LegacyGaEventNames.LESSON_OPENED,
  [AnalyticsEvents.NEXT_LESSON]: LegacyGaEventNames.NEXT_LESSON,
  [AnalyticsEvents.COMMUNITY_COURSE_OPENED]: LegacyGaEventNames.COMMUNITY_COURSE_OPENED,
  [AnalyticsEvents.FOLLOW]: LegacyGaEventNames.FOLLOW,
  [AnalyticsEvents.UNFOLLOW]: LegacyGaEventNames.UNFOLLOW,
  [AnalyticsEvents.COURSE_MENTIONED]: LegacyGaEventNames.COURSE_MENTIONED,
  [AnalyticsEvents.EVENT_MENTIONED]: LegacyGaEventNames.EVENT_MENTIONED,
  [AnalyticsEvents.CHANNEL_MENTIONED]: LegacyGaEventNames.CHANNEL_MENTIONED,
  [AnalyticsEvents.GO_LIVE_SESSION_JOINED]: LegacyGaEventNames.GO_LIVE_SESSION_JOINED,
  [AnalyticsEvents.USER_BANNED]: LegacyGaEventNames.USER_BANNED,
  [AnalyticsEvents.MEDIA_REMOVED]: LegacyGaEventNames.MEDIA_REMOVED,
  [AnalyticsEvents.EVENT_ATTENDEES_VIEWED]: LegacyGaEventNames.EVENT_ATTENDEES_VIEWED,
  [AnalyticsEvents.EVENT_ATTENDEES_LIST_EXPORTED]: LegacyGaEventNames.EVENT_ATTENDEES_LIST_EXPORTED,
  [AnalyticsEvents.MEMBER_VIEWED_OWN_ANSWERS]: LegacyGaEventNames.MEMBER_VIEWED_OWN_ANSWERS,
  [AnalyticsEvents.ADMIN_VIEWED_MEMBER_ANSWERS]: LegacyGaEventNames.ADMIN_VIEWED_MEMBER_ANSWERS,
  [AnalyticsEvents.CERTIFICATE_DOWNLOAD]: LegacyGaEventNames.CERTIFICATE_DOWNLOAD,
  [AnalyticsEvents.EVENT_RSVP]: LegacyGaEventNames.EVENT_RSVP,
  [AnalyticsEvents.PAGE_VIEW]: 'page_view',
  [AnalyticsEvents.ELEMENT_CLICKED]: 'element_clicked',
  [AnalyticsEvents.ERROR_OCCURRED]: 'error_occurred',
}

const BACKEND_EVENT_NAMES: Record<string, string> = {
  [AnalyticsEvents.USER_AUTO_LOGOUT]: 'client-portal-user-auto-logout',
  [AnalyticsEvents.COMMUNITIES_USER_AUTO_LOGOUT]: 'communities-user-auto-logout',
  [AnalyticsEvents.GENERATE_TOKEN_FAILED]: 'generate-token-failed',
  [AnalyticsEvents.GENERATE_TOKEN_SUCCESS]: 'generate-token-success',
  [AnalyticsEvents.COMMUNITIES_GROUP_ACCESS]: 'communities-group-access-event',
  [AnalyticsEvents.NO_COMMUNITIES_FOUND]: 'no-communities-found',
  [AnalyticsEvents.COURSE_ACCESS_REFRESH]: 'course-access-refresh',
  [AnalyticsEvents.PUSH_NOTIFICATION_INITIALIZED]: 'client-portal-initialize-push-event',
  [AnalyticsEvents.FORCE_UPDATE_EVENT]: 'client-portal-force-app-update-event',
  [AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR]: 'analytics-context-resolver-error',
}

const ANONYMOUS_CAPABLE_GA_EVENTS = new Set<string>([
  AnalyticsEvents.PAGE_VIEW,
  AnalyticsEvents.ELEMENT_CLICKED,
  AnalyticsEvents.ERROR_OCCURRED,
])
const IMMEDIATE_CONTEXT_FREE_GA_EVENTS = new Set<string>([AnalyticsEvents.ELEMENT_CLICKED, AnalyticsEvents.ERROR_OCCURRED])

export function resolveProviderEventName(provider: AnalyticsProviderKey, event: string): string | null {
  if (provider === 'backendAnalytics') return BACKEND_EVENT_NAMES[event] ?? null
  if (provider === 'pendoAnalytics') return event === AnalyticsEvents.PAGE_VIEW ? event : null
  return GA_EVENT_NAMES[event] ?? sanitizeGaEventName(event)
}

const delivery = (
  provider: AnalyticsProviderKey,
  eventName: string,
  access: AnalyticsAccess = 'authenticated',
  mode: AnalyticsDeliveryMode = 'transactional',
  context: AnalyticsContextMode = 'route',
  requiresIdentity: AnalyticsIdentityRequirement = access === 'authenticated' ? 'route-contact' : 'none'
): AnalyticsDeliveryPolicy => ({ provider, eventName, access, delivery: mode, context, requiresIdentity })

export function resolveEventDeliveries(event: string): AnalyticsDeliveryPolicy[] {
  if (event === AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR) {
    return [delivery('backendAnalytics', BACKEND_EVENT_NAMES[event]!, 'anonymous', 'immediate', 'none')]
  }

  const gaEventName = resolveProviderEventName('googleAnalytics', event)
  if (!gaEventName) return []

  const deliveries: AnalyticsDeliveryPolicy[] = []
  if (event === AnalyticsEvents.PAGE_VIEW) {
    deliveries.push(delivery('pendoAnalytics', event, 'anonymous'))
  }
  const isImmediateContextFree = IMMEDIATE_CONTEXT_FREE_GA_EVENTS.has(event)
  deliveries.push(
    delivery(
      'googleAnalytics',
      gaEventName,
      ANONYMOUS_CAPABLE_GA_EVENTS.has(event) ? 'anonymous' : 'authenticated',
      isImmediateContextFree ? 'immediate' : 'transactional',
      isImmediateContextFree ? 'none' : 'route'
    )
  )

  const backendEventName = resolveProviderEventName('backendAnalytics', event)
  if (backendEventName) deliveries.push(delivery('backendAnalytics', backendEventName))

  return deliveries
}
